import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatEuro, formatPercent, formatNumber } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/common/PageHeader";
import KpiCard from "@/components/common/KpiCard";
import StatusBadge from "@/components/common/StatusBadge";
import { TID } from "@/constants/testIds";
import { Building, DoorOpen, Landmark, Coins, TrendingUp, RotateCcw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function DashboardPage() {
  const { user, organizations, activeOrgId } = useAuth();
  const activeOrg = organizations.find((o) => o.id === activeOrgId);
  const [kpis, setKpis] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      api.get("/dashboard/kpis"),
      api.get("/subscription"),
      api.get("/buildings"),
      api.get("/companies"),
    ]).then(([k, s, b, c]) => {
      if (!mounted) return;
      setKpis(k.data.kpis);
      setSubscription(s.data);
      setBuildings(b.data.items || []);
      setCompanies(c.data.items || []);
    }).catch(() => {}).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [activeOrgId]);

  const isDemo = activeOrg?.is_demo;

  const resetDemo = async () => {
    try {
      await api.post("/demo/reset");
      toast.success("Données démo réinitialisées");
      window.location.reload();
    } catch {
      toast.error("Réinitialisation impossible (compte non démo)");
    }
  };

  return (
    <div data-testid={TID.dashboard} className="animate-fade-up">
      <PageHeader
        overline={`Bienvenue, ${user?.first_name || ""}`}
        title="Tableau de bord"
        description={activeOrg ? `Vue consolidée de ${activeOrg.name}.` : "Vue consolidée du patrimoine et des locations."}
        actions={
          <>
            {isDemo && (
              <Button variant="outline" onClick={resetDemo} data-testid={TID.demoResetBtn} className="btn-pill">
                <RotateCcw className="mr-2 h-4 w-4" /> Réinitialiser les données démo
              </Button>
            )}
          </>
        }
      />

      {isDemo && (
        <div className="mb-8 rounded-2xl border border-accent/30 bg-accent/5 px-5 py-3.5 text-sm flex items-center gap-3">
          <Sparkles className="h-4 w-4 text-accent" />
          <span><strong>Compte de démonstration</strong> — toutes les données affichées sont fictives.</span>
        </div>
      )}

      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6">
        <KpiCard name="patrimoine" label="Valeur du patrimoine" icon={Landmark} tone="accent"
                 value={loading ? "…" : formatEuro(kpis?.patrimoine_value)} />
        <KpiCard name="rent-monthly" label="Loyers mensuels" icon={Coins}
                 value={loading ? "…" : formatEuro(kpis?.monthly_rent_total)} />
        <KpiCard name="occupancy" label="Taux d'occupation" icon={TrendingUp}
                 value={loading ? "…" : formatPercent(kpis?.occupancy_rate)} />
        <KpiCard name="lots" label="Biens gérés" icon={DoorOpen}
                 value={loading ? "…" : formatNumber(kpis?.lots_count)} />
      </section>

      <section className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="card-elevated p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-overline">Patrimoine</div>
              <h2 className="mt-1 font-heading text-xl">Immeubles</h2>
            </div>
            <StatusBadge tone="info">{buildings.length} au total</StatusBadge>
          </div>
          <div className="mt-6 divide-y divide-border">
            {buildings.length === 0 && !loading && (
              <div className="py-10 text-center text-muted-foreground text-sm">Aucun immeuble pour l'instant.</div>
            )}
            {buildings.map((b) => (
              <div key={b.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 gap-2">
                <div>
                  <div className="font-heading text-foreground">{b.name}</div>
                  <div className="text-xs text-muted-foreground">{b.address}, {b.postal_code} {b.city}</div>
                </div>
                <div className="text-sm text-right">
                  <div className="font-mono text-foreground">{formatEuro(b.current_value || b.acquisition_price)}</div>
                  <div className="text-xs text-muted-foreground">Valeur estimée</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="card-elevated p-6">
            <div className="text-overline">Formule</div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-serif text-3xl capitalize">{subscription?.plan || "—"}</span>
              <StatusBadge tone={subscription?.status === "trial" ? "warning" : "success"}>
                {subscription?.status === "trial" ? "Essai" : subscription?.status || "Actif"}
              </StatusBadge>
            </div>
            <div className="mt-4 space-y-2 text-sm text-muted-foreground">
              <div className="flex justify-between"><span>Utilisateurs max.</span><span className="text-foreground font-medium">{subscription?.features?.max_users}</span></div>
              <div className="flex justify-between"><span>Immeubles max.</span><span className="text-foreground font-medium">{subscription?.features?.max_buildings}</span></div>
              <div className="flex justify-between"><span>Assistant IA</span><span className="text-foreground font-medium">{subscription?.features?.assistant_ai ? "Inclus" : "Non inclus"}</span></div>
            </div>
          </div>

          <div className="card-elevated p-6">
            <div className="text-overline">Sociétés</div>
            <h2 className="mt-1 font-heading text-xl">{companies.length} entité{companies.length > 1 ? "s" : ""}</h2>
            <ul className="mt-4 space-y-3">
              {companies.slice(0, 4).map((c) => (
                <li key={c.id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate max-w-[180px]">{c.name}</span>
                  </div>
                  <StatusBadge tone={c.legal_form === "HOLDING" ? "accent" : "neutral"}>{c.legal_form}</StatusBadge>
                </li>
              ))}
              {companies.length === 0 && <li className="text-sm text-muted-foreground">Aucune société.</li>}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
